class ShippingService {
  constructor({
    geoCacheKey = 'geoData',
    maxTries = 12,
    pollDelayMs = 500,
  } = {}) {
    this.geoCacheKey = geoCacheKey;
    this.maxTries = maxTries;
    this.pollDelayMs = pollDelayMs;
  }

  /* --------------------
     GEO
  -------------------- */
  async fetchGeoData() {
    const cached = sessionStorage.getItem(this.geoCacheKey);
    if (cached) return JSON.parse(cached);

    const res = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to fetch GeoData');

    const data = await res.json();
    if (data?.error) throw new Error(data?.reason || 'IP API error');

    sessionStorage.setItem(this.geoCacheKey, JSON.stringify(data));
    return data;
  }

  mapGeoToShippingAddress(geo) {
    return {
      country: geo.country_name || geo.country || '',
      province: geo.region || geo.region_code || '',
      zip: geo.postal || '',
      city: geo.city || '',
    };
  }

  /* --------------------
     SHIPPING RATES
  -------------------- */
  // Perf #3: a single cart:update fans out to several callers (the cart:update listener, cart-progress-
  // bar.handleCartUpdate, and the reco add path), each previously starting an independent up-to-6s
  // shipping poll. Debounce so they collapse into ONE run reflecting the final cart state. All callers
  // within the window share the same pending promise (so none hang); the trailing call wins.
  getShippingRates() {
    if (this._shipDebounce) clearTimeout(this._shipDebounce);
    if (!this._shipPending) {
      this._shipPending = new Promise((resolve, reject) => {
        this._shipResolve = resolve;
        this._shipReject = reject;
      });
    }
    this._shipDebounce = setTimeout(() => {
      const resolve = this._shipResolve;
      const reject = this._shipReject;
      this._shipPending = this._shipResolve = this._shipReject = null;
      this._runShippingRates().then(resolve, reject);
    }, 150);
    return this._shipPending;
  }

  async _runShippingRates() {
    const geo = await this.fetchGeoData();
    const address = this.mapGeoToShippingAddress(geo);

    if (!address.country || !address.zip) {
      throw new Error('Country and ZIP are required for shipping rates');
    }

    await this.prepareShippingRates(address);
    const rates = await this.pollShippingRates(address);

    const el = document.querySelector('.shipping-items-price span');
    const t = cartShippingStrings();

    if (!rates || !rates.length) {
      if (el) el.textContent = t.calculatedAtCheckout;
      return;
    }

    const cheapest = this.pickCheapest(rates);
    const price = Number(cheapest.price);
    // A $0 rate (free-shipping threshold met) reads as FREE; a real rate shows its price; anything
    // unresolvable falls back to "Calculated at checkout". Labels are translatable (locales/*.json).
    let formatted;
    if (price === 0) {
      formatted = t.free;
    } else {
      formatted = this.formatMoney(price, this.resolveCurrency(geo, cheapest)) || t.calculatedAtCheckout;
    }

    if (el) el.textContent = formatted;

    return rates;

  }

  async prepareShippingRates(address) {
    
    const dynamicParams = new URLSearchParams({
      'shipping_address[country]': address.country,
      'shipping_address[province]': address.province,
      'shipping_address[zip]': address.zip,
      ...(address.city ? { 'shipping_address[city]': address.city } : {}),
    });

    const fallbackParams = new URLSearchParams({
      'shipping_address[country]': shippingFallbackCountry,
      'shipping_address[province]': shippingFallbackRegion,
      'shipping_address[zip]': address.zip || '',
      ...(address.city ? { 'shipping_address[city]': address.city } : {}),
    });

    let res = await fetch(`/cart/prepare_shipping_rates.json?${dynamicParams.toString()}`, {
      method: 'POST',
    });

    if (!res.ok) {
      console.warn('Dynamic shipping rate address failed, retrying with fallback address');
      res = await fetch(`/cart/prepare_shipping_rates.json?${fallbackParams.toString()}`, {
        method: 'POST',
      });

      if (!res.ok) {
        throw new Error(`prepare_shipping_rates failed with fallback (${res.status})`);
      }else{
        address.country = shippingFallbackCountry;
        address.province = shippingFallbackRegion;
      }
    }

  }

  async pollShippingRates(address) {

    const params = new URLSearchParams({
      'shipping_address[country]': address.country,
      'shipping_address[province]': address.province,
      'shipping_address[zip]': address.zip,
      ...(address.city ? { 'shipping_address[city]': address.city } : {}),
    });

    const url = `/cart/async_shipping_rates.json?${params.toString()}`;

    for (let attempt = 0; attempt < this.maxTries; attempt++) {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`async_shipping_rates failed (${res.status})`);

      const data = await res.json();
      if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) {
        return data.shipping_rates;
      }

      await this.sleep(this.pollDelayMs);
    }

    throw new Error('Timed out waiting for shipping rates');
  }

  /* --------------------
     HELPERS
  -------------------- */
  pickCheapest(rates) {
    return rates.reduce((min, r) =>
      Number(r.price) < Number(min.price) ? r : min
    );
  }

  resolveCurrency(geo, rate) {
    return (
      rate?.currency ||
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.shop ||
      geo?.currency ||
      'USD'
    );
  }

  formatMoney(amountDecimal, currencyCode) {
    if (!Number.isFinite(amountDecimal)) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(amountDecimal);
    } catch {
      return `${amountDecimal.toFixed(2)} ${currencyCode}`;
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}


// Translatable shipping labels, read from cart-drawer-component[data-i18n] (locales/*.json), with
// English fallbacks so this works even if the bundle is absent.
function cartShippingStrings() {
  const drawer = document.querySelector('cart-drawer-component');
  let t = {};
  if (drawer) {
    try { t = JSON.parse(drawer.getAttribute('data-i18n') || '{}'); } catch (e) { t = {}; }
  }
  return {
    free: t.free || 'FREE',
    calculatedAtCheckout: t.calculatedAtCheckout || 'Calculated at checkout',
  };
}

const shippingService = new ShippingService();

// Run on load AND after every cart change — the cart summary is re-rendered (morphSection) on
// cart:update, which resets the shipping line to its server default, so the rate must be re-applied.
// Shipping also genuinely depends on cart contents (weight / free-shipping threshold), so re-fetching
// is correct, not just cosmetic. Any failure falls back to "Calculated at checkout".
function applyShippingRate() {
  shippingService.getShippingRates().catch(function (err) {
    console.warn(err);
    var el = document.querySelector('.shipping-items-price span');
    if (el) el.textContent = cartShippingStrings().calculatedAtCheckout;
  });
}

applyShippingRate();
document.addEventListener('cart:update', applyShippingRate);

